import { computed, onScopeDispose, readonly, ref, shallowRef, watch } from "vue";
import { toBoolean, toNumber } from "#ce/utils/props";
import { isRemoteSearch } from "#ce/components/SpQuantumSelect/props";
import { createRemoteSearchApi } from "#ce/components/SpQuantumSelect/api/remote-search";
import { mergeRemotePage, validateRemotePage } from "#ce/components/SpQuantumSelect/composables/remote-pages";

/** Values of the remote state's `status`. */
export const RemoteStatus = Object.freeze({
  IDLE: "idle",
  TOO_SHORT: "too-short",
  DEBOUNCING: "debouncing",
  LOADING: "loading",
  LOADING_MORE: "loading-more",
  READY: "ready",
  EMPTY: "empty",
  ERROR: "error",
});

/** Statuses with a page request in flight. */
export const LOADING_STATUSES = Object.freeze([RemoteStatus.LOADING, RemoteStatus.LOADING_MORE]);

/** Statuses after a page has been accepted. */
export const SETTLED_STATUSES = Object.freeze([RemoteStatus.READY, RemoteStatus.EMPTY]);

const CACHE_LIMIT = 20;
const CACHE_TTL_MS = 60000;
const DEFAULT_DEBOUNCE_MS = 250;

/** Injection key for the remote state QuantumSelect provides to its containers. */
export const quantumSelectRemoteKey = Symbol("quantum-select-remote");

function nonnegativeNumber(value, fallback) {
  const number = toNumber(value);

  return Number.isFinite(number) && number >= 0 ? number : fallback;
}

/** Per-instance root search lifecycle. Query changes invalidate requests synchronously, before debounce. */
export function useQuantumSelectRemote(props, { shown, disabled, query, composing }) {
  const enabled = computed(() => isRemoteSearch(props));
  const minimum = computed(() =>
    Math.max(
      Math.floor(nonnegativeNumber(props.remoteMinimumInputLength, 0)),
      toBoolean(props.remoteSearchOnly) ? 1 : 0,
    ),
  );
  const debounce = computed(() => nonnegativeNumber(props.remoteDebounce, DEFAULT_DEBOUNCE_MS));
  const noCache = computed(() => toBoolean(props.remoteNoCache));
  const loader = computed(() => {
    if (props.remoteLoader != null) {
      return props.remoteLoader;
    }

    return props.remoteUrl ? createRemoteSearchApi(props.remoteUrl) : null;
  });
  const sourceVersion = ref(0);
  const acceptedLoad = shallowRef(null);
  const items = shallowRef([]);
  const state = shallowRef({
    status: RemoteStatus.IDLE,
    query: currentQuery(),
    page: 0,
    hasMore: false,
    error: null,
  });
  const cache = new Map();
  let generation = 0;
  let controller;
  let timer;
  let pending = false;
  let failedPage = 1;
  let disposed = false;
  let resettingQuery = false;

  function currentQuery() {
    return String(query.value ?? "");
  }

  function publish(status, changes = {}) {
    state.value = Object.freeze({ ...state.value, ...changes, status });
  }

  function invalidate() {
    generation++;
    clearTimeout(timer);
    controller?.abort();
    controller = undefined;
    pending = false;
  }

  function active() {
    return !disposed && enabled.value && shown.value && !disabled.value && !composing.value;
  }

  function eligible() {
    return [...currentQuery()].length >= minimum.value;
  }

  function remember() {
    if (noCache.value) {
      return;
    }

    const key = state.value.query;
    cache.delete(key);
    cache.set(key, { items: items.value, state: state.value, at: Date.now() });

    if (cache.size > CACHE_LIMIT) {
      cache.delete(cache.keys().next().value);
    }
  }

  function restore() {
    const key = currentQuery();
    const saved = cache.get(key);

    if (!saved || noCache.value) {
      return false;
    }

    if (Date.now() - saved.at >= CACHE_TTL_MS) {
      cache.delete(key);

      return false;
    }

    cache.delete(key);
    cache.set(key, saved);
    items.value = saved.items;
    state.value = saved.state;

    return true;
  }

  async function request(page) {
    if (!active() || !eligible() || pending) {
      return;
    }

    if (typeof loader.value !== "function") {
      publish(RemoteStatus.ERROR, { error: { kind: "configuration" } });

      return;
    }

    const id = generation;
    const text = currentQuery();
    const source = loader.value;
    const abort = new AbortController();

    pending = true;
    failedPage = page;
    controller = abort;
    publish(page === 1 ? RemoteStatus.LOADING : RemoteStatus.LOADING_MORE, { error: null });

    function release() {
      if (controller === abort) {
        pending = false;
        controller = undefined;
      }
    }

    function current() {
      return (
        id === generation && active() && !abort.signal.aborted && text === currentQuery() && source === loader.value
      );
    }

    try {
      const reply = validateRemotePage(await source(text, { page, signal: abort.signal }));

      if (!current()) {
        return;
      }

      const merged = mergeRemotePage(page === 1 ? [] : items.value, reply.items, {
        hasMore: reply.hasMore,
        itemValue: props.itemValue,
        itemTitle: props.itemTitle,
        selectableBranches: toBoolean(props.selectableBranches),
      });
      items.value = merged;
      publish(merged.length ? RemoteStatus.READY : RemoteStatus.EMPTY, { page, hasMore: reply.hasMore, error: null });

      if (!current()) {
        return;
      }

      remember();
      release();
      acceptedLoad.value = Object.freeze({ query: text, page, items: items.value, hasMore: reply.hasMore });
    } catch (error) {
      if (!current()) {
        return;
      }

      const kind = ["configuration", "protocol"].includes(error?.kind) ? error.kind : "network";
      const sanitized = { kind };

      if (Number.isInteger(error?.status)) {
        sanitized.status = error.status;
      }

      release();
      publish(RemoteStatus.ERROR, { error: Object.freeze(sanitized) });
    } finally {
      release();
    }
  }

  function start(immediate = false) {
    invalidate();
    items.value = [];
    publish(RemoteStatus.IDLE, { query: currentQuery(), page: 0, hasMore: false, error: null });

    if (!active()) {
      return;
    }

    if (typeof loader.value !== "function") {
      publish(RemoteStatus.ERROR, { error: { kind: "configuration" } });

      return;
    }

    if (!eligible()) {
      publish(RemoteStatus.TOO_SHORT);

      return;
    }

    if (restore()) {
      return;
    }

    if (immediate || debounce.value === 0) {
      request(1);
    } else {
      publish(RemoteStatus.DEBOUNCING);
      timer = setTimeout(loadFirstPage, debounce.value);
    }
  }

  function loadFirstPage() {
    request(1);
  }

  function handleQuery() {
    if (resettingQuery) {
      return;
    }

    start();
  }

  function handleVisibility() {
    start(true);
  }

  function handleSource() {
    sourceVersion.value++;
    cache.clear();
    start(true);
  }

  function loadMore() {
    const { status, hasMore, page } = state.value;

    if (status === RemoteStatus.READY && hasMore) {
      return request(page + 1);
    }
  }

  function retryRemote() {
    if (state.value.status === RemoteStatus.ERROR) {
      return request(failedPage);
    }
  }

  function reloadRemote() {
    handleSource();
  }

  function resetRemote() {
    invalidate();
    resettingQuery = true;

    try {
      query.value = "";
    } finally {
      resettingQuery = false;
    }

    start(true);
  }

  function dispose() {
    disposed = true;
    invalidate();
    cache.clear();
  }

  watch([query, minimum, debounce, composing], handleQuery, { flush: "sync" });
  watch([shown, disabled], handleVisibility, { flush: "sync" });
  watch([loader, () => props.remoteNoCache], handleSource, { flush: "sync" });
  onScopeDispose(dispose);
  start(true);

  return {
    sourceVersion: readonly(sourceVersion),
    acceptedLoad: readonly(acceptedLoad),
    enabled,
    minimum,
    items: readonly(items),
    state: readonly(state),
    loadMore,
    retryRemote,
    reloadRemote,
    resetRemote,
  };
}
